// Terrengskygge-overlegg — hvilke områder ligger i skygge på et gitt tidspunkt.
//
// Operativt formål: skjul og dekning (hvor kan vi bevege oss uten å stå i sol),
// og motlys/eksponering for OP-poster (når blir posten synlig fordi sola treffer
// den, eller når står vi i motlys mot en observatør).
//
// Regnemotoren er `@skade/terrain`: `loadElevationGrid` (ekte Terrarium-DEM,
// samme fliser kartets relieff bruker) + `cellShadowState`/`terrainShadow`
// (horisontvinkel mot solas asimut) + `sunPosition`. Denne modulen henter data,
// deler regnejobben i biter og tegner resultatet — den regner ingen fysikk selv.
//
// HVA OVERLEGGET FAKTISK ER (må stå i UI-et, UX_DESIGNSYSTEM §10):
//   · BINÆR KJERNESKYGGE fra TERRENG (DTM). Ingen bygninger, ingen skog, ingen
//     halvskygge og ingen diffust himmellys. En celle som er merket «solbelyst»
//     kan ligge midt i granskog og i praksis være mørk; en celle i skygge er
//     ikke svart, den får fortsatt 10–20 % diffust lys på klar dag.
//   · Horisontal oppløsning i kildedata er ~30 m. Smale skjæringer, voller og
//     enkeltbygg finnes ikke i grunnlaget uansett hvor fint vi resampler.
//   · Sol under horisonten = NATT. Da er alt i skygge, og det vises som natt
//     (overlegget slås av + tydelig status), ikke som «hele området er skjult».
//
// YTELSE: skyggeberegningen er O(rader·kolonner·strålelengde). Vi (1) velger
// oppløsning etter arealet slik at cellebudsjettet holdes, (2) regner radvis i
// biter med pust mellom slik at nettleseren aldri låses, og (3) viser fremdrift.
import type { Map as MlMap, ImageSource } from 'maplibre-gl';
import type { ElevationGrid } from '@skade/contracts';
import {
  NODATA,
  SHADED,
  bboxAround,
  cellShadowState,
  gridCoverage,
  gridDimsForBBox,
  latToMercatorPixelY,
  loadElevationGrid,
  mercatorPixelYToLat,
  sunPosition,
  type BBox,
  type ShadowMask,
  type TerrainShadowOptions,
} from '@skade/terrain';
import '../lysforhold.css';

const SOURCE = 'skygge-terreng';
const LAYER = 'skygge-terreng-raster';

// ── Oppløsning og cellebudsjett (ren logikk) ─────────────────────────────────

/**
 * Cellebudsjett: hvor mange celler vi tillater i skyggeberegningen. 60 000
 * celler (≈245×245) med ~250 stegs stråler er ca. 15 mill. høydeoppslag —
 * omtrent ett sekund CPU, delt i biter så UI-et holder seg responsivt.
 */
export const MAKS_CELLER = 60_000;

/** Fineste oppløsning vi går til. Under 25 m er det ren interpolasjon (DTM ~30 m). */
export const FINESTE_CELLE_M = 25;

export interface Oppløsning {
  celleM: number;
  rows: number;
  cols: number;
}

/**
 * Velg cellestørrelse slik at rutenettet holder seg innenfor cellebudsjettet.
 * Degraderer oppløsningen i stedet for å låse nettleseren på et stort utsnitt —
 * og resultatet vises i UI-et, så operatøren vet hvor grovt det er regnet.
 */
export function velgOppløsning(
  bbox: BBox,
  maksCeller = MAKS_CELLER,
  finesteCelleM = FINESTE_CELLE_M,
): Oppløsning {
  let celleM = finesteCelleM;
  for (let i = 0; i < 40; i++) {
    const { rows, cols } = gridDimsForBBox(bbox, celleM);
    if (rows * cols <= maksCeller) return { celleM: Math.round(celleM), rows, cols };
    // Antall celler går som 1/celleM²; skalér direkte mot budsjettet (+10 % margin).
    celleM *= Math.sqrt((rows * cols) / maksCeller) * 1.05;
  }
  const { rows, cols } = gridDimsForBBox(bbox, celleM);
  return { celleM: Math.round(celleM), rows, cols };
}

/**
 * Hvor langt strålen mot sola skal marsjeres, i celler. Skyggen fra en 500 m høy
 * ås er 5,7 km lang ved 5° solhøyde, så ~8 km rekkevidde dekker realistiske
 * tilfeller i Oslofjord-området uten å betale for hele rutenettet.
 */
export function maksStråleCeller(celleM: number, rekkeviddeM = 8000): number {
  return Math.min(400, Math.max(40, Math.round(rekkeviddeM / Math.max(1, celleM))));
}

// ── Skyggeberegning i biter (samme resultat som terrainShadow) ───────────────

export interface BitOpsjoner {
  /** Rader per bit før vi puster. Lavere = jevnere UI, litt mer overhead. */
  raderPerBit?: number;
  /** Fremdrift 0–1. */
  fremdrift?: (andel: number) => void;
  /** true ⇒ avbryt (nytt tidspunkt valgt). Beregningen kaster ikke, den slutter. */
  avbrutt?: () => boolean;
  /** Pust mellom bitene. Default: makrotask (setTimeout 0). Tester bruker no-op. */
  pause?: () => Promise<void>;
}

/**
 * Terrengskygge for hele rutenettet, regnet RADVIS MED PUST slik at nettleseren
 * ikke låses. Bruker `cellShadowState` fra @skade/terrain per celle, altså samme
 * kodevei som `terrainShadow` — resultatet er identisk (verifisert i test).
 *
 * Er sola på/under horisonten returneres alt som skygge uten å røre DEM-en:
 * det er natt, og da er svaret kjent på forhånd.
 */
export async function beregnSkyggeIBiter(
  g: ElevationGrid,
  opts: TerrainShadowOptions,
  bit: BitOpsjoner = {},
): Promise<ShadowMask> {
  const raderPerBit = Math.max(1, bit.raderPerBit ?? 8);
  const pause = bit.pause ?? (() => new Promise<void>((r) => setTimeout(r, 0)));
  const state = new Uint8Array(g.rows * g.cols);
  const sunBelowHorizon = opts.sunElevationDeg <= 0;
  let withData = 0;
  let shaded = 0;

  for (let row = 0; row < g.rows; row++) {
    for (let col = 0; col < g.cols; col++) {
      const s = cellShadowState(g, row, col, opts);
      state[row * g.cols + col] = s;
      if (s !== NODATA) {
        withData++;
        if (s === SHADED) shaded++;
      }
    }
    if ((row + 1) % raderPerBit === 0 && row + 1 < g.rows) {
      bit.fremdrift?.((row + 1) / g.rows);
      await pause();
      if (bit.avbrutt?.()) break;
    }
  }
  bit.fremdrift?.(1);
  return {
    rows: g.rows,
    cols: g.cols,
    state,
    sunBelowHorizon,
    shadedFraction: withData === 0 ? 0 : shaded / withData,
    sunAzimuthDeg: opts.sunAzimuthDeg,
    sunElevationDeg: opts.sunElevationDeg,
  };
}

// ── Maske → bilde (ren logikk) ──────────────────────────────────────────────

/** Geo-referansen masken hører til (delmengde av `ElevationGrid`). */
export interface MaskeGeo {
  north: number;
  west: number;
  latStep: number;
  lonStep: number;
}

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Tegn masken til RGBA for et MapLibre `image`-lag.
 *
 * VIKTIG PROJEKSJON: et image-lag strekkes LINEÆRT i Web-Mercator mellom de fire
 * hjørnene, mens `ElevationGrid` er ekvirektangulært (konstant breddegrad-steg).
 * Derfor regnes hver BILDERAD tilbake til breddegrad via invers Mercator før vi
 * slår opp i rutenettet. Uten dette forskyves skyggen i nord-sør-retning, mest
 * midt i utsnittet — og en skygge på feil sted er verre enn ingen skygge.
 *
 * Bare SHADED tegnes. Solbelyst og hull er HELT gjennomsiktige, slik at kartet
 * fortsatt er helten (UX_DESIGNSYSTEM §2) og «ingen data» ikke forveksles med
 * «sol».
 */
export function maskeTilRgba(
  maske: ShadowMask,
  geo: MaskeGeo,
  bbox: BBox,
  bredde: number,
  høyde: number,
  farge: Rgb,
  alfa = 150,
): Uint8ClampedArray {
  const ut = new Uint8ClampedArray(bredde * høyde * 4);
  const yN = latToMercatorPixelY(bbox.north, 0, 1);
  const yS = latToMercatorPixelY(bbox.south, 0, 1);
  for (let j = 0; j < høyde; j++) {
    const py = yN + ((j + 0.5) / høyde) * (yS - yN);
    const lat = mercatorPixelYToLat(py, 0, 1);
    const row = Math.round((geo.north - lat) / geo.latStep);
    if (row < 0 || row >= maske.rows) continue;
    for (let i = 0; i < bredde; i++) {
      const lon = bbox.west + ((i + 0.5) / bredde) * (bbox.east - bbox.west);
      const col = Math.round((lon - geo.west) / geo.lonStep);
      if (col < 0 || col >= maske.cols) continue;
      if (maske.state[row * maske.cols + col] !== SHADED) continue;
      const o = (j * bredde + i) * 4;
      ut[o] = farge.r;
      ut[o + 1] = farge.g;
      ut[o + 2] = farge.b;
      ut[o + 3] = alfa;
    }
  }
  return ut;
}

/** Bildeoppløsning: følg rutenettet, men hold deg innenfor fornuftige grenser. */
export function bildeStørrelse(rows: number, cols: number): { bredde: number; høyde: number } {
  const klem = (v: number) => Math.min(1024, Math.max(64, v));
  return { bredde: klem(cols), høyde: klem(rows) };
}

/** #rrggbb → RGB. Ugyldig verdi gir `fallback` (vi gjetter aldri på en farge). */
export function hexTilRgb(hex: string, fallback: Rgb = { r: 10, g: 12, b: 10 }): Rgb {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Lineær blanding av to farger. `andel` = hvor mye av `a` som beholdes. */
export function blandRgb(a: Rgb, b: Rgb, andel: number): Rgb {
  const t = Math.min(1, Math.max(0, andel));
  const m = (x: number, y: number) => Math.round(x * t + y * (1 - t));
  return { r: m(a.r, b.r), g: m(a.g, b.g), b: m(a.b, b.b) };
}

// ── Status (ærlige tekster, vises ordrett) ──────────────────────────────────

export type SkyggeStatus =
  | 'av'
  | 'laster-dem'
  | 'regner'
  | 'ok'
  | 'natt'
  | 'ingen-data'
  | 'feil';

export const SKYGGE_STATUS_TEKST: Record<SkyggeStatus, string> = {
  av: 'Skyggelaget er av. Slå det på for å regne terrengskygge for utsnittet.',
  'laster-dem': 'Henter høydedata (Terrarium-fliser) for utsnittet …',
  regner: 'Regner terrengskygge …',
  ok: 'Terrengskygge regnet for utsnittet.',
  natt: 'Sola er under horisonten — det er NATT. Alt ligger i skygge, så det er ingen skygge å tegne.',
  'ingen-data': 'Ingen høydedata for utsnittet (fliser mangler eller ingen nettverk). Laget er tomt — dette er datagrunnlaget, ikke terrenget.',
  feil: 'Kunne ikke hente høydedata. Laget er tomt.',
};

/** Andel i skygge som lesbar linje. */
export function skyggeAndelTekst(maske: ShadowMask): string {
  return `${Math.round(maske.shadedFraction * 100)} % av området ligger i terrengskygge.`;
}

/** Solretning som lesbar linje (asimut + høyde). */
export function solretningTekst(elevasjonDeg: number, asimutDeg: number): string {
  if (elevasjonDeg <= 0) {
    return `Sol ${elevasjonDeg.toFixed(1)}° (under horisonten) · asimut ${Math.round(asimutDeg)}°`;
  }
  return `Sol ${elevasjonDeg.toFixed(1)}° høyde · asimut ${Math.round(asimutDeg)}° (skyggen faller mot ${Math.round((asimutDeg + 180) % 360)}°)`;
}

// ── Tidsskyver (ren logikk) ─────────────────────────────────────────────────

/** Starten på UTC-døgnet et tidspunkt ligger i. */
export function døgnStartMs(tidMs: number): number {
  const d = new Date(tidMs);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Minutt-på-døgnet (0–1439) for tidsskyveren. */
export function minuttPåDøgn(tidMs: number): number {
  return Math.floor((tidMs - døgnStartMs(tidMs)) / 60_000);
}

/** Tidspunkt fra døgnstart + minutt. */
export function tidFraMinutt(døgnStart: number, minutt: number): number {
  return døgnStart + Math.min(1439, Math.max(0, Math.round(minutt))) * 60_000;
}

/** «TT:MM» i UTC (Z) — samme formatering som lysforhold-panelet. */
export function hhmmUtc(tidMs: number): string {
  return new Date(tidMs).toISOString().slice(11, 16);
}

// ── Laget + kontrollpanelet ─────────────────────────────────────────────────

export interface SkyggeHooks {
  /** Området skyggen regnes for — default: kartets utsnitt. */
  bounds: () => BBox;
  /** Overstyrbar «nå» (test / tidsmaskin). */
  now?: () => number;
  /** Injiserbar flis-fetch (proxy, test, nettleser-verifikasjon uten nett). */
  fetchImpl?: typeof fetch;
  /** Injiserbar DEM-kilde. Default: `loadElevationGrid` (Terrarium). */
  lastRutenett?: (bbox: BBox, celleM: number) => Promise<ElevationGrid>;
  /** Kort melding til operatøren (shell.showAlarm). */
  varsle?: (tekst: string) => void;
}

/**
 * Terrengskygge-lag med tidsskyver. `el` er kontrollpanelet (docked GNÅ-panel);
 * selve skyggen ligger som et `image`-lag i kartet.
 */
export class SkyggeLag {
  readonly el: HTMLElement;
  /** Kalles ved tilstandsendring (så verktøylinja kan speile av/på). */
  onStateChange?: () => void;

  private kartKlart = false;
  private lagLagt = false;
  private på = false;
  private status: SkyggeStatus = 'av';
  private tidMs: number;
  private døgnStart: number;
  private generasjon = 0;
  private cache: { nøkkel: string; grid: ElevationGrid } | null = null;
  private sisteMaske: ShadowMask | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;

  private sub!: HTMLElement;
  private tidVerdi!: HTMLElement;
  private skyver!: HTMLInputElement;
  private solLinje!: HTMLElement;
  private fremdriftBar!: HTMLElement;
  private fremdriftWrap!: HTMLElement;
  private statusLinje!: HTMLElement;
  private detaljLinje!: HTMLElement;

  constructor(private map: MlMap, private hooks: SkyggeHooks) {
    this.tidMs = this.nå();
    this.døgnStart = døgnStartMs(this.tidMs);
    this.el = this.bygg();
    if (map.loaded?.()) this.kartKlart = true;
    else map.on?.('load', () => { this.kartKlart = true; });
    // Panorering/zoom flytter utsnittet: uten dette ville overlegget bli stående
    // igjen på det GAMLE området — en skygge på feil sted. Debounce i
    // `planleggBeregning` gjør at bare den siste posisjonen regnes.
    map.on?.('moveend', () => { if (this.på) this.planleggBeregning(400); });
  }

  private nå(): number {
    return this.hooks.now ? this.hooks.now() : Date.now();
  }

  erPå(): boolean {
    return this.på;
  }

  /** Åpne/lukke panelet OG laget (én bevisst handling — laget er av som standard). */
  toggle() {
    this.på = !this.på;
    this.el.classList.toggle('open', this.på);
    this.onStateChange?.();
    if (this.på) {
      this.settTidspunkt(this.nå());
    } else {
      this.generasjon++;
      this.settSynlig(false);
      this.settStatus('av');
    }
  }

  /** Sett tidspunktet skyggen regnes for (kan kobles til tidsmaskinen). */
  settTidspunkt(tidMs: number) {
    this.tidMs = tidMs;
    this.døgnStart = døgnStartMs(tidMs);
    this.skyver.value = String(minuttPåDøgn(tidMs));
    this.oppdaterTidTekst();
    this.planleggBeregning(0);
  }

  // ── Kart ──────────────────────────────────────────────────────────────────

  /**
   * Kilde + lag opprettes FØRST når vi har et ekte bilde å vise. En
   * plassholder-PNG er fristende, men MapLibre dekoder kilde-URL-en med én gang
   * og en tom/ugyldig plassholder gir «The source image could not be decoded» i
   * konsollen — funnet i nettleser-røyktesten.
   */
  private sikreLag(url: string, bbox: BBox) {
    if (this.lagLagt || !this.kartKlart) return;
    if (this.map.getSource?.(SOURCE)) { this.lagLagt = true; return; }
    this.map.addSource(SOURCE, {
      type: 'image',
      url,
      coordinates: hjørner(bbox),
    });
    this.map.addLayer(
      {
        id: LAYER,
        type: 'raster',
        source: SOURCE,
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': 1,
          // Ingen utjevning: cellene er 25–200 m og skal SE grove ut. Et glatt
          // bilde ville antydet en presisjon datagrunnlaget ikke har.
          'raster-resampling': 'nearest',
          'raster-fade-duration': 0,
        },
      },
      førsteSymbollag(this.map),
    );
    this.lagLagt = true;
  }

  private settSynlig(synlig: boolean) {
    if (!this.lagLagt) return;
    try {
      this.map.setLayoutProperty?.(LAYER, 'visibility', synlig ? 'visible' : 'none');
    } catch {
      /* laget er borte (stilbytte) — neste beregning legger det på igjen */
    }
  }

  // ── Beregning ─────────────────────────────────────────────────────────────

  private planleggBeregning(forsinkelseMs = 180) {
    if (!this.på) return;
    if (this.debounce) clearTimeout(this.debounce);
    // Debounce: mens skyveren dras skal bare siste posisjon regnes ut.
    this.debounce = setTimeout(() => { void this.beregn(); }, forsinkelseMs);
  }

  private async beregn() {
    if (!this.på) return;
    // Generasjonsteller: en ny beregning gjør den forrige uinteressant, så
    // bitene fra en gammel jobb skal slutte i stedet for å skrive over kartet.
    this.generasjon += 1;
    const min = this.generasjon;
    const avbrutt = () => this.generasjon !== min;

    const bbox = normaliserBBox(this.hooks.bounds());
    const midtLat = (bbox.north + bbox.south) / 2;
    const midtLon = (bbox.west + bbox.east) / 2;
    const sol = sunPosition(this.tidMs, midtLat, midtLon);
    this.solLinje.textContent = solretningTekst(sol.elevationDeg, sol.azimuthDeg);

    // Natt: svaret er kjent uten DEM. Vis det som natt, ikke som «alt skjult».
    if (sol.elevationDeg <= 0) {
      this.settSynlig(false);
      this.sisteMaske = null;
      this.settStatus('natt');
      this.visFremdrift(null);
      return;
    }

    const { celleM, rows, cols } = velgOppløsning(bbox);
    this.detaljLinje.textContent =
      `Oppløsning ${celleM} m/celle · ${rows}×${cols} celler `
      + `(kildedata er ~30 m DTM — finere tall er interpolasjon, ikke ny detalj).`;

    let grid: ElevationGrid;
    const nøkkel = `${bbox.north.toFixed(4)},${bbox.south.toFixed(4)},${bbox.west.toFixed(4)},${bbox.east.toFixed(4)},${celleM}`;
    if (this.cache && this.cache.nøkkel === nøkkel) {
      grid = this.cache.grid;
    } else {
      this.settStatus('laster-dem');
      this.visFremdrift(0);
      try {
        grid = await this.lastGrid(bbox, celleM);
      } catch {
        this.settStatus('feil');
        this.visFremdrift(null);
        this.settSynlig(false);
        return;
      }
      if (avbrutt()) return;
      if (gridCoverage(grid) === 0) {
        this.settStatus('ingen-data');
        this.visFremdrift(null);
        this.settSynlig(false);
        return;
      }
      this.cache = { nøkkel, grid };
    }

    this.settStatus('regner');
    const maske = await beregnSkyggeIBiter(
      grid,
      {
        sunAzimuthDeg: sol.azimuthDeg,
        sunElevationDeg: sol.elevationDeg,
        maxCells: maksStråleCeller(celleM),
      },
      {
        fremdrift: (a) => { if (!avbrutt()) this.visFremdrift(a); },
        avbrutt,
      },
    );
    if (avbrutt()) return;
    this.sisteMaske = maske;
    this.tegn(maske, grid, bbox);
    this.visFremdrift(null);
    this.settStatus('ok');
  }

  private async lastGrid(bbox: BBox, celleM: number): Promise<ElevationGrid> {
    if (this.hooks.lastRutenett) return this.hooks.lastRutenett(bbox, celleM);
    return loadElevationGrid(bbox, {
      targetMetersPerCell: celleM,
      ...(this.hooks.fetchImpl ? { fetchImpl: this.hooks.fetchImpl } : {}),
    });
  }

  private tegn(maske: ShadowMask, grid: ElevationGrid, bbox: BBox) {
    const { bredde, høyde } = bildeStørrelse(maske.rows, maske.cols);
    const rgba = maskeTilRgba(maske, grid, bbox, bredde, høyde, this.skyggefarge());
    const url = rgbaTilPngUrl(rgba, bredde, høyde);
    if (!url) {
      this.settStatus('feil');
      return;
    }
    // Et basiskart-bytte kan ha fjernet kilden vår; da må laget legges på igjen.
    if (this.lagLagt && !this.map.getSource?.(SOURCE)) this.lagLagt = false;
    this.sikreLag(url, bbox);
    if (!this.lagLagt) return; // kartet er ikke ferdig lastet ennå
    const src = this.map.getSource?.(SOURCE) as ImageSource | undefined;
    src?.updateImage({ url, coordinates: hjørner(bbox) });
    this.settSynlig(true);
  }

  /** Rutenettet som ligger til grunn — til QA/verifikasjon (jf. `statusNå`). */
  rutenettInfo(): { rows: number; cols: number; dekning: number; minM: number; maxM: number } | null {
    const g = this.cache?.grid;
    if (!g) return null;
    let minM = Infinity;
    let maxM = -Infinity;
    for (let i = 0; i < g.rows * g.cols; i++) {
      const v = g.data[i];
      if (v === -32768) continue;
      if (v < minM) minM = v;
      if (v > maxM) maxM = v;
    }
    return {
      rows: g.rows,
      cols: g.cols,
      dekning: gridCoverage(g),
      minM: Number.isFinite(minM) ? minM : 0,
      maxM: Number.isFinite(maxM) ? maxM : 0,
    };
  }

  /**
   * Skyggefargen leses fra designsystemets tokens (theme.css) i stedet for å
   * hardkodes — et raster kan ikke bruke `var()` direkte, men det kan lese
   * tokenet.
   *
   * Hvorfor `--info` blandet ned i `--surface` og ikke bare `--surface`: en rein
   * mørk flate er USYNLIG på det mørke GNÅ-basiskartet (verifisert i nettleser —
   * første forsøk ga et overlegg man ikke kunne se). En nedtonet kald blåtone
   * leses på alle tre basiskartene (mørkt vektorkart, lyst Kartverket-topo,
   * ortofoto) og er dessuten kartografisk konvensjon for skygge.
   */
  private skyggefarge(): Rgb {
    try {
      const stil = getComputedStyle(document.documentElement);
      const info = hexTilRgb(stil.getPropertyValue('--info'), { r: 91, g: 155, b: 213 });
      const flate = hexTilRgb(stil.getPropertyValue('--surface'));
      return blandRgb(info, flate, 0.55);
    } catch {
      return blandRgb({ r: 91, g: 155, b: 213 }, hexTilRgb(''), 0.55);
    }
  }

  // ── DOM ───────────────────────────────────────────────────────────────────

  private bygg(): HTMLElement {
    const p = div('report-composer skygge-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Terrengskygge — skjul og motlys'));
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'report-x';
    x.textContent = '×';
    x.title = 'Lukk (slår av skyggelaget)';
    x.setAttribute('aria-label', 'Lukk terrengskygge');
    x.onclick = () => { if (this.på) this.toggle(); };
    head.append(x);
    p.append(head);

    this.sub = div('lys-sub');
    p.append(this.sub);

    const tidRad = div('sky-tidrad');
    this.tidVerdi = span('sky-tid', '--:--');
    const nåBtn = document.createElement('button');
    nåBtn.type = 'button';
    nåBtn.className = 'sky-btn';
    nåBtn.textContent = 'Nå';
    nåBtn.title = 'Sett tidspunktet til nå';
    nåBtn.onclick = () => this.settTidspunkt(this.nå());
    tidRad.append(this.tidVerdi, nåBtn);
    p.append(tidRad);

    this.skyver = document.createElement('input');
    this.skyver.type = 'range';
    this.skyver.className = 'sky-skyver';
    this.skyver.min = '0';
    this.skyver.max = '1439';
    this.skyver.step = '5';
    this.skyver.setAttribute('aria-label', 'Tidspunkt på døgnet (UTC)');
    this.skyver.oninput = () => {
      this.tidMs = tidFraMinutt(this.døgnStart, Number(this.skyver.value));
      this.oppdaterTidTekst();
      this.planleggBeregning();
    };
    p.append(this.skyver);

    this.solLinje = div('sky-sol');
    p.append(this.solLinje);

    this.fremdriftWrap = div('sky-fremdrift');
    this.fremdriftBar = div('sky-fremdrift-bar');
    this.fremdriftWrap.append(this.fremdriftBar);
    p.append(this.fremdriftWrap);

    this.statusLinje = div('sky-status');
    p.append(this.statusLinje);

    this.detaljLinje = div('sky-detalj');
    p.append(this.detaljLinje);

    // Tegnforklaring: bare skyggen har farge, resten av kartet står urørt.
    const legend = div('sky-legend');
    const rad = div('sky-legend-rad');
    rad.append(span('sky-swatch', ''), span('sky-legend-tekst', 'I terrengskygge'));
    const rad2 = div('sky-legend-rad');
    rad2.append(span('sky-swatch tom', ''), span('sky-legend-tekst', 'Solbelyst / ingen høydedata (ikke fargelagt)'));
    legend.append(rad, rad2);
    p.append(legend);

    const note = document.createElement('ul');
    note.className = 'lys-forbehold';
    for (const t of [
      'Dette er BINÆR KJERNESKYGGE fra terreng (DTM): ingen bygninger, ingen skog, '
      + 'ingen halvskygge. En «solbelyst» celle i granskog er i praksis skyggelagt.',
      'En celle i skygge er ikke svart — på klar dag får den fortsatt 10–20 % '
      + 'diffust himmellys.',
      'Kildedata er ~30 m DTM. Skjæringer, voller og enkeltbygg finnes ikke i grunnlaget.',
      'Bruk lysforhold-panelet for BMNT/EENT, månelys og NVG — dette laget svarer '
      + 'bare på hvor terrenget skygger.',
    ]) {
      const li = document.createElement('li');
      li.textContent = t;
      note.append(li);
    }
    p.append(note);

    this.oppdaterTidTekst();
    this.settStatus('av');
    return p;
  }

  private oppdaterTidTekst() {
    this.tidVerdi.textContent = `${hhmmUtc(this.tidMs)} Z`;
    this.sub.textContent = `${new Date(this.tidMs).toISOString().slice(0, 10)} · tidspunkt for skyggen (UTC)`;
  }

  private settStatus(s: SkyggeStatus) {
    const forrige = this.status;
    this.status = s;
    this.statusLinje.textContent = SKYGGE_STATUS_TEKST[s];
    this.statusLinje.classList.toggle('advarsel', s === 'ingen-data' || s === 'feil');
    if (s === 'ok' && this.sisteMaske) {
      this.statusLinje.textContent = `${SKYGGE_STATUS_TEKST.ok} ${skyggeAndelTekst(this.sisteMaske)}`;
    }
    // Et tomt lag skal ikke bare stå stille og se «ferdig» ut: si det i klartekst
    // også utenfor panelet, én gang per overgang.
    if (s !== forrige && (s === 'ingen-data' || s === 'feil')) {
      this.hooks.varsle?.(SKYGGE_STATUS_TEKST[s]);
    }
    this.onStateChange?.();
  }

  /** Nåværende status — til test/verifikasjon og dock-speiling. */
  statusNå(): SkyggeStatus {
    return this.status;
  }

  private visFremdrift(andel: number | null) {
    if (andel === null) {
      this.fremdriftWrap.classList.remove('aktiv');
      return;
    }
    this.fremdriftWrap.classList.add('aktiv');
    this.fremdriftBar.style.width = `${Math.round(Math.min(1, Math.max(0, andel)) * 100)}%`;
  }
}

// ── Kart- og bildehjelpere ──────────────────────────────────────────────────

/** Monokromt linjeikon til verktøylinja (ås med skyggeside). */
export const SKYGGE_IKON =
  '<svg viewBox="0 0 20 20" width="18" height="18" stroke="currentColor" fill="none" '
  + 'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M2 15.5h16"/><path d="M4 15.5l5-7 4.5 7z"/>'
  + '<path d="M9 8.5v7" opacity="0.6"/><path d="M13.5 4.5a2.5 2.5 0 1 1 3 3"/></svg>';

/** Hjørner i MapLibre-rekkefølge: NV, NØ, SØ, SV. */
function hjørner(b: BBox): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    [b.west, b.north],
    [b.east, b.north],
    [b.east, b.south],
    [b.west, b.south],
  ];
}

/**
 * Klem utsnittet til noe som kan regnes på. Et helt utzoomet kart gir en boks
 * på flere hundre km; da ville selv grov oppløsning bli meningsløs. Vi kapper
 * til 60 km rundt senteret og sier det i detalj-linja via `velgOppløsning`.
 */
export function normaliserBBox(b: BBox, maksSpennKm = 60): BBox {
  const høydeKm = (b.north - b.south) * 111.32;
  const breddeKm = (b.east - b.west) * 111.32 * Math.cos((((b.north + b.south) / 2) * Math.PI) / 180);
  if (høydeKm <= maksSpennKm && breddeKm <= maksSpennKm) return b;
  return bboxAround(
    { lat: (b.north + b.south) / 2, lon: (b.west + b.east) / 2 },
    (maksSpennKm / 2) * 1000,
  );
}

/** Første symbol-lag i stilen — skyggen legges UNDER etiketter og enhetssymboler. */
function førsteSymbollag(map: MlMap): string | undefined {
  try {
    const lag = map.getStyle?.()?.layers ?? [];
    return lag.find((l) => l.type === 'symbol')?.id;
  } catch {
    return undefined;
  }
}

/** RGBA → data-URL (PNG) via canvas. null hvis canvas ikke er tilgjengelig. */
function rgbaTilPngUrl(rgba: Uint8ClampedArray, bredde: number, høyde: number): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bredde;
    canvas.height = høyde;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // `createImageData` + `set` framfor `new ImageData(rgba, …)`: unngår at
    // bufferets type (ArrayBuffer vs SharedArrayBuffer) må matche konstruktøren.
    const bilde = ctx.createImageData(bredde, høyde);
    bilde.data.set(rgba);
    ctx.putImageData(bilde, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function div(cls: string): HTMLElement {
  const e = document.createElement('div');
  e.className = cls;
  return e;
}

function span(cls: string, text: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = text;
  return e;
}
